import type { IPresentation, IPresentationLink } from "./models";

export interface IPlayerMetadata {
    requirePlayerPassword: boolean;
    embedLinkUserIdToken: string;
}

/**
 * The subset of a presentation link that is safe to hand to any viewer
 * (link ids, password/embed uids, invite emails, Salesforce contacts and
 * meeting PINs stay server-side).
 */
export interface IPlayerPresentationLink {
    id: string;
    type: IPresentationLink["type"];
    allowCommenting?: boolean;
    /** Whether Salesforce tracking is enabled for the link (never the contact map itself) */
    salesforce: boolean;
}

export interface IPlayerContext {
    workspaceId: string;
    /**
     * Only the presentation fields the player needs. `userId` is included
     * only for the owner and collaborators.
     */
    presentation: Partial<IPresentation> & {
        id: string;
        link?: IPlayerPresentationLink;
    };
    slidesMetadata: {
        [slideId: string]: {
            isSkipped: boolean;
            modifiedAt: number;
        };
    };
    creator: {
        photoURL: string | null;
        displayName: string | null;
        /** Whether the creator is a beautiful.ai employee (used to filter internal views from analytics) */
        isInternal: boolean;
    };
    isOwner: boolean;
    isCollaborator: boolean;
    canEditPresentation: boolean;
    showBranding: boolean;
    showGetDeckUI: boolean;
    /** Whether the deck is owned by one of the internal marketing accounts */
    isMarketingDeck: boolean;
    slug: string;
}

export enum PlayerAnalyticsQueries {
    VIEWS_PER_DAY = "viewsPerDay",
    DOWNLOADS_PER_DAY = "downloadsPerDay",
    LINK_SUMMARY = "linkSummary",
    AGGREGATES = "aggregates",
    SUMMARY = "summary",
    RECENT_PLAYS = "recentPlays",
    RECENT_DOWNLOADS = "recentDownloads",
    VIEWING_AGGREGATES = "viewingAggregates",
    SESSION_DETAIL = "sessionDetail",
}

export enum PlayerSalesforceEvents {
    PLAYER_OPENED = "player-opened",
    PLAYER_CLOSED = "player-closed",
    PLAYER_PROGRESS = "player-progress",
}
